import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMenyerDishesListThunk } from "../store/slices/dishesReducer";
import { Link } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
// import DishCard from "../components/Cards/DishCard_v1";
import DishCard from "../components/Cards/DishCard";

const openDish = (dish) => {
  if (dish?.public_share_token) {
    window.location.href = `/public/items/${dish.public_share_token}`;
    return;
  }
  if (dish?.id) {
    window.location.href = `/items/${dish.id}`;
  }
};

const goToProductPage = (dish) => {
  const gtin = dish?.gtin ? String(dish.gtin).trim() : "";
  if (!gtin) return;

  // In browser builds only REACT_APP_* env vars are exposed.
  // Fallback keeps local dev working when only BACKEND_URL exists in .env.
  const backendProductBase =
    process.env.REACT_APP_BACKEND_PRODUCT_URL;

  if (!backendProductBase) return;
  const productUrl = `${backendProductBase.replace(/\/$/, "")}/products/${encodeURIComponent(gtin)}`;
  window.open(productUrl, "_blank", "noopener,noreferrer");
};

const MenyerPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { menyerDishesList } = useSelector((state) => state.dishes);
  const [darkMode, setDarkMode] = useState(false);
  const { auth } = useSelector((state) => state.affiliate);
  
  // Custom scrollbar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Custom thin scrollbar for the entire page */
      ::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }
      
      ::-webkit-scrollbar-track {
        background: ${darkMode ? '#1f2937' : '#f3f4f6'};
        border-radius: 3px;
      }
      
      ::-webkit-scrollbar-thumb {
        background: ${darkMode ? '#6b7280' : '#d1d5db'};
        border-radius: 3px;
        transition: background 0.2s ease;
      }
      
      ::-webkit-scrollbar-thumb:hover {
        background: ${darkMode ? '#9ca3af' : '#9ca3af'};
      }
      
      ::-webkit-scrollbar-corner {
        background: ${darkMode ? '#1f2937' : '#f3f4f6'};
      }
      
      /* Firefox scrollbar */
      * {
        scrollbar-width: thin;
        scrollbar-color: ${darkMode ? '#6b7280 #1f2937' : '#d1d5db #f3f4f6'};
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, [darkMode]);
  
  // State for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState({
    filters: false,
    dagensMeny: false,
    allaRecept: false
  });
  const [filters, setFilters] = useState({
    search: "",
    mealType: "all",
    day: "all",
    dietary: "all",
  });
  const [currentPage, setCurrentPage] = useState(1);

  const dishes = menyerDishesList.data || [];
  const loading = menyerDishesList.isLoading;
  const error = menyerDishesList.error;
  const pagination = menyerDishesList.pagination;

  // Affiliate data using actual user data from auth
  const affiliateData = {
    name: auth.name,
    logo: auth.image || "/assets/images/logo_low_res.png" // Use user's image or fallback to logo
  };

  useEffect(() => {
    dispatch(fetchMenyerDishesListThunk({ page: currentPage }));
  }, [dispatch, currentPage]);

  const isInitialLoading = loading && dishes.length === 0;

  const handlePageChange = (nextPage) => {
    if (!pagination) return;
    if (nextPage < 1 || nextPage > pagination.last_page) return;
    setCurrentPage(nextPage);
  };

  // Toggle collapsible section
  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Helper function to get meal type tags with status
  const getMealTypeTags = (dish) => {
    const mealTypes = dish.meal_types || [];

    return {
      F: mealTypes.includes("frukost"),
      L: mealTypes.includes("lunch"),
      M: mealTypes.includes("middag"),
      MM: mealTypes.includes("mellanmål"),
      SM: mealTypes.includes("småmål"),
      KM: mealTypes.includes("kvällsmål"),
      D: mealTypes.includes("desser"),
      Ö: mealTypes.includes("övrigt"),
    };
  };

  // Group dishes by categories
  const dagensMeny = dishes.filter(
    (dish) =>
      dish.is_dagens_meny === true ||
      dish.is_dagens_meny === 1 ||
      dish.is_dagens_meny === "1"
  );

  // All other dishes (not dagens meny)
  const allOtherDishes = dishes.filter((dish) => {
    const isDagensMeny =
      dish.is_dagens_meny === true ||
      dish.is_dagens_meny === 1 ||
      dish.is_dagens_meny === "1";
    return !isDagensMeny;
  });

  const days = useMemo(
    () => ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
    []
  );

  const getDayName = (day) => {
    const dayNames = {
      monday: "Måndag",
      tuesday: "Tisdag",
      wednesday: "Onsdag",
      thursday: "Torsdag",
      friday: "Fredag",
      saturday: "Lördag",
      sunday: "Söndag",
    };
    return dayNames[day] || day;
  };

  const filteredAllOtherDishes = useMemo(() => {
    const query = filters.search.trim().toLowerCase();

    return allOtherDishes.filter((dish) => {
      if (query) {
        const searchableValues = [
          dish?.item_name,
          dish?.item_name_sv,
          dish?.title,
          dish?.name,
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        if (!searchableValues.some((value) => value.includes(query))) {
          return false;
        }
      }

      if (filters.mealType !== "all") {
        const mealTypes = dish.meal_types || [];
        if (!mealTypes.includes(filters.mealType)) return false;
      }

      if (filters.day !== "all") {
        const dayTags = dish.day_tags || [];
        if (!dayTags.includes(filters.day)) return false;
      }

      if (filters.dietary === "vegan" && !dish.is_vegan) return false;
      if (filters.dietary === "vegetarian" && !dish.is_vegetarian) return false;
      if (filters.dietary === "animal" && !dish.is_animal) return false;

      return true;
    });
  }, [allOtherDishes, filters]);

  const activeFilterCount = useMemo(
    () =>
      Object.entries(filters).filter(([key, value]) =>
        key === "search" ? value.trim().length > 0 : value !== "all"
      ).length,
    [filters]
  );

  const clearFilters = () => {
    setFilters({
      search: "",
      mealType: "all",
      day: "all",
      dietary: "all",
    });
  };

  // Enhanced DishCard component with tags
  //To make separate card for dagens meny with future scale in this individual area
  const EnhancedTodayDishCard = ({ dish, onClick, darkMode }) => {
    const mealTypeTags = getMealTypeTags(dish);

    return (
      <DishCard
        item={dish}
        onClick={onClick}
        onProductClick={() => goToProductPage(dish)}
        darkMode={darkMode}
        mealTypeTags={mealTypeTags}
      />
    );
  };
  const EnhancedDishCard = ({ dish, onClick, darkMode }) => {
    const mealTypeTags = getMealTypeTags(dish);

    return (
      <DishCard
        item={dish}
        onClick={onClick}
        onProductClick={() => goToProductPage(dish)}
        darkMode={darkMode}
        mealTypeTags={mealTypeTags}
      />
    );
  };

  // Collapsible section component
  const CollapsibleSection = ({ 
    title, 
    subtitle, 
    isCollapsed, 
    onToggle, 
    children, 
    itemCount = 0 
  }) => (
    <div className="mb-8">
      <div 
        className={`flex items-center justify-between p-4 rounded-t-lg cursor-pointer transition-all duration-300 ${
          darkMode 
            ? 'bg-gray-800/50 hover:bg-gray-800/70 border border-gray-700 border-b-0' 
            : 'bg-white/70 hover:bg-white/90 border border-gray-200 border-b-0 shadow-sm'
        }`}
        onClick={onToggle}
      >
        <div className="flex-1">
          <h2 className={`text-2xl md:text-3xl font-bold mb-1 ${
            darkMode ? "text-white" : "text-gray-900"
          }`}>
            {title}
          </h2>
          <p className={`text-base ${
            darkMode ? "text-gray-300" : "text-gray-600"
          }`}>
            {subtitle} {itemCount > 0 && `(${itemCount} recept)`}
          </p>
        </div>
        <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
          darkMode 
            ? 'bg-gray-700 hover:bg-gray-600' 
            : 'bg-gray-100 hover:bg-gray-200'
        }`}>
          {isCollapsed ? (
            <img 
              src="/assets/icons/Plus.png" 
              alt="Expand" 
              className="w-5 h-5"
            />
          ) : (
            <img 
              src="/assets/icons/Dolj.png" 
              alt="Collapse" 
              className="w-5 h-5"
            />
          )}
        </div>
      </div>
      
      <div className={`overflow-y-auto overflow-x-hidden transition-all duration-500 ease-in-out ${
        isCollapsed ? 'max-h-0 opacity-0' : 'max-h-screen opacity-100'
      }`}>
        <div className={`p-6 rounded-b-lg border border-t-0 ${
          darkMode 
            ? 'bg-gray-800/30 border-gray-700' 
            : 'bg-white/50 border-gray-200'
        } shadow-sm`}>
          {children}
        </div>
      </div>
    </div>
  );

  if (isInitialLoading) {
    return (
      <div
        className={`min-h-screen relative ${
          darkMode
            ? "bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800"
            : "bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50"
        }`}
        style={{
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Animated background pattern overlay */}
        <div className="fixed inset-0 pointer-events-none opacity-30" style={{ zIndex: 0 }}>
          <div className={`absolute inset-0 ${
            darkMode 
              ? 'bg-gradient-radial from-blue-900/10 via-transparent to-transparent'
              : 'bg-gradient-radial from-blue-200/20 via-transparent to-transparent'
          }`} style={{
            backgroundImage: darkMode 
              ? 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)'
              : 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)'
          }}></div>
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0" style={{
            backgroundImage: darkMode
              ? 'linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)'
              : 'linear-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.02) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="flex items-center justify-center h-screen relative" style={{ zIndex: 1 }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p
              className={`text-lg ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Laddar menyer...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && dishes.length === 0) {
    return (
      <div
        className={`min-h-screen relative ${
          darkMode
            ? "bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800"
            : "bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50"
        }`}
        style={{
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Animated background pattern overlay */}
        <div className="fixed inset-0 pointer-events-none opacity-30" style={{ zIndex: 0 }}>
          <div className={`absolute inset-0 ${
            darkMode 
              ? 'bg-gradient-radial from-blue-900/10 via-transparent to-transparent'
              : 'bg-gradient-radial from-blue-200/20 via-transparent to-transparent'
          }`} style={{
            backgroundImage: darkMode 
              ? 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)'
              : 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)'
          }}></div>
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0" style={{
            backgroundImage: darkMode
              ? 'linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)'
              : 'linear-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.02) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 py-8 relative" style={{ zIndex: 1 }}>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md mx-auto mt-20 shadow-lg">
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Fel vid laddning av menyer
            </h3>
            <p className="text-red-600 mb-4">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen relative ${
        darkMode
          ? "bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800"
          : "bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50"
      }`}
      style={{
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Animated background pattern overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-30" style={{ zIndex: 0 }}>
        <div className={`absolute inset-0 ${
          darkMode 
            ? 'bg-gradient-radial from-blue-900/10 via-transparent to-transparent'
            : 'bg-gradient-radial from-blue-200/20 via-transparent to-transparent'
        }`} style={{
          backgroundImage: darkMode 
            ? 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)'
        }}></div>
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: darkMode
            ? 'linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)'
            : 'linear-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.02) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>
      
      {/* Content wrapper with relative positioning */}
      <div className="relative" style={{ zIndex: 1 }}>
      {/* Single sticky menyer bar: full-width strip was z-[1200] while controls were z-50 — controls sat underneath and looked “missing” */}
      <header className="fixed top-0 left-0 right-0 z-[1200] bg-gray-100/95 backdrop-blur shadow-none border-b border-gray-200">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 tablet:px-6 laptop:px-8">
          <div className="flex min-w-0 items-center space-x-3">
            <div className="h-10 w-10 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
              {affiliateData.logo && affiliateData.logo !== "/assets/images/logo_low_res.png" ? (
                <img 
                  src={affiliateData.logo} 
                  alt={affiliateData.name}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <img 
                src="/assets/icons/Innehall.png" 
                alt="" 
                className={`w-6 h-6 text-gray-400 ${affiliateData.logo && affiliateData.logo !== "/assets/images/logo_low_res.png" ? 'hidden' : 'block'}`} 
              />
            </div>
            <div className="min-w-0">
              <h1 className={`truncate font-semibold text-md ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {affiliateData.name}
              </h1>
              <p className={`text-xs ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Inloggad
              </p>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center space-x-3">
            <button
              type="button"
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? "bg-gray-400 text-yellow-400 hover:bg-gray-100"
                  : "bg-gray-400 text-yellow-400 hover:bg-gray-200"
              }`}
              title="Byt tema"
            >
              {darkMode ? (
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            <Link
              to="/dashboard"
              className="p-2 rounded-lg bg-gray-400 text-gray-300 hover:bg-gray-500 hover:text-white transition-colors"
              title="Inställningar"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 pt-24">
        <div
          className={`relative transition-opacity duration-200 ${
            loading && dishes.length > 0 ? "opacity-60 pointer-events-none" : ""
          }`}
          aria-busy={loading}
        >
        

        {/* 1. Dagens Meny Section */}
        {dagensMeny.length > 0 && (
          <CollapsibleSection
            title="Dagens Meny"
            subtitle="Våra specialrecept för idag"
            isCollapsed={collapsedSections.dagensMeny}
            onToggle={() => toggleSection("dagensMeny")}
            itemCount={dagensMeny.length}
          >
            <div className="grid grid-cols-3 md:grid-cols-3 mobile:grid-cols-1 tablet:grid-cols-2 gap-6">
              {dagensMeny.map((dish) => (
                <EnhancedTodayDishCard
                  key={dish.id}
                  dish={dish}
                  onClick={() => openDish(dish)}
                  darkMode={darkMode}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

<CollapsibleSection
          title="Filter"
          subtitle="Hitta rätter snabbt baserat på preferenser."
          isCollapsed={collapsedSections.filters}
          onToggle={() => toggleSection("filters")}
          itemCount={activeFilterCount}
        >
          <div className="flex flex-col gap-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                  activeFilterCount === 0
                    ? darkMode
                      ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : darkMode
                    ? "bg-gray-700 text-gray-100 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Återställ filter ({activeFilterCount})
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Sök recept..."
                className={`w-full rounded-lg border px-3 py-2 text-sm ${
                  darkMode
                    ? "bg-gray-900/70 border-gray-700 text-white placeholder:text-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                }`}
              />

              <select
                value={filters.mealType}
                onChange={(e) => setFilters((prev) => ({ ...prev, mealType: e.target.value }))}
                className={`w-full rounded-lg border px-3 py-2 text-sm ${
                  darkMode ? "bg-gray-900/70 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="all">Alla måltider</option>
                <option value="frukost">Frukost</option>
                <option value="lunch">Lunch</option>
                <option value="middag">Middag</option>
                <option value="mellanmål">Mellanmål</option>
                <option value="småmål">Småmål</option>
                <option value="kvällsmål">Kvällsmål</option>
                <option value="desser">Desser</option>
                <option value="övrigt">Övrigt</option>
              </select>

              <select
                value={filters.day}
                onChange={(e) => setFilters((prev) => ({ ...prev, day: e.target.value }))}
                className={`w-full rounded-lg border px-3 py-2 text-sm ${
                  darkMode ? "bg-gray-900/70 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="all">Alla dagar</option>
                {days.map((day) => (
                  <option key={day} value={day}>
                    {getDayName(day)}
                  </option>
                ))}
              </select>

              <select
                value={filters.dietary}
                onChange={(e) => setFilters((prev) => ({ ...prev, dietary: e.target.value }))}
                className={`w-full rounded-lg border px-3 py-2 text-sm ${
                  darkMode ? "bg-gray-900/70 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="all">Alla kostpreferenser</option>
                <option value="vegan">Vegansk</option>
                <option value="vegetarian">Vegetarisk</option>
                <option value="animal">Animaliskt</option>
              </select>
            </div>
          </div>
        </CollapsibleSection>

        {/* 2. Alla Recept Section */}
        {allOtherDishes.length > 0 && (
          <CollapsibleSection
            title={t("Alla Recept")}
            subtitle={t("Utforska alla våra läckra recept")}
            isCollapsed={collapsedSections.allaRecept}
            onToggle={() => toggleSection('allaRecept')}
            itemCount={filteredAllOtherDishes.length}
          >
              {activeFilterCount > 0 && (
                <div className={`mb-4 text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Visar {filteredAllOtherDishes.length} av {allOtherDishes.length} recept
                </div>
              )}
              <div className="space-y-0 grid grid-cols-3 laptop:grid-cols-3 mobile:grid-cols-1 tablet:grid-cols-2 gap-4">
              {filteredAllOtherDishes.map((dish) => (
                <EnhancedDishCard
                  key={dish.id}
                  dish={dish}
                  onClick={() => openDish(dish)}
                  darkMode={darkMode}
                />
              ))}
            </div>
            {filteredAllOtherDishes.length === 0 && (
              <div className={`mt-6 text-center py-8 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                Inga recept matchar dina filter.
              </div>
            )}
          </CollapsibleSection>
        )}

        </div>

        {pagination && pagination.last_page > 1 && (
          <div
            className={`mt-8 mb-4 flex flex-col gap-3 rounded-lg border px-4 py-4 sm:flex-row sm:items-center sm:justify-between ${
              darkMode
                ? "border-gray-700 bg-gray-800/50"
                : "border-gray-200 bg-white/80 shadow-sm"
            }`}
          >
            {error && (
              <p className={`text-sm sm:col-span-2 ${darkMode ? "text-red-300" : "text-red-600"}`}>
                {error}
              </p>
            )}
            <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              {t("Visar")} {pagination.from || 0}–{pagination.to || 0} {t("av")}{" "}
              {pagination.total || 0} {t("recept")}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={loading || pagination.current_page <= 1}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  darkMode
                    ? "border-gray-600 text-gray-100 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t("Föregående")}
              </button>
              <span className={`px-2 text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                {loading ? t("Laddar...") : `${t("Sida")} ${pagination.current_page} ${t("av")} ${pagination.last_page}`}
              </span>
              <button
                type="button"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={loading || pagination.current_page >= pagination.last_page}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  darkMode
                    ? "border-gray-600 text-gray-100 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t("Nästa")}
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {dishes.length === 0 && !loading && (
          <div className="text-center py-20">
            <div
              className={`${darkMode ? "text-gray-400" : "text-gray-300"} mb-8`}
            >
              <img src="/assets/icons/Innehall.png" alt="No recipes" className="w-24 h-24 mx-auto" />
            </div>
            <h3
              className={`text-2xl font-bold mb-4 ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {t("Inga recept tillgängliga")}
            </h3>
            <p
              className={`${
                darkMode ? "text-gray-400" : "text-gray-600"
              } max-w-md mx-auto`}
            >
              {t("Vi arbetar på att lägga till fler läckra recept. Kom tillbaka snart!")}
            </p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default MenyerPage;
